import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Banknote,
  Car,
  Clock,
  MapPin,
  MonitorSmartphone,
  MousePointerClick,
} from "lucide-react";
import { BentoCard, GhostButton, RydeCta, RydeFade, RydeFooter, RydeNav, SectionTag } from "@/components/ryde/Ryde";

const FLOW = ["Brand books a campaign", "Ads play on Slate 01 screens", "GPS verifies playtime", "Owners get paid on the 5th"];

const RULES = [
  [MapPin, "Your city", "Campaigns go live city by city. Busy metros see demand first."],
  [Clock, "Your hours", "More verified hours on the road means more verified playtime."],
  [MousePointerClick, "Live inventory", "No live campaign means no payout that month. The tablet is still yours."],
];

const STEPS = [
  {
    icon: MonitorSmartphone,
    title: "Get Slate 01",
    text: "The 10.1\" Android tablet is the key to the network. No Slate, no seat — the hardware is what carries the ads.",
  },
  {
    icon: Car,
    title: "Clip it in, drive",
    text: "Inside the rear windshield, player facing traffic. GPS + 4G verify when and where ads actually played.",
  },
  {
    icon: Banknote,
    title: "Paid on the 5th",
    text: "When campaigns ran on your screen, earnings land by UPI or bank transfer on the 5th of the month.",
  },
];

export default function RydeWorld() {
  return (
    <main className="min-h-screen bg-[#0B0D0C] font-body text-[#F4F7F5]" data-testid="world-page">
      <RydeNav />

      <section className="px-5 pb-20 pt-32 md:px-8 md:pt-44" data-track-section="world-hero">
        <div className="mx-auto max-w-6xl">
          <RydeFade>
            <SectionTag testId="world-hero-tag">The RydeCast network</SectionTag>
            <h1 className="mt-6 max-w-3xl font-display text-5xl font-black leading-[0.98] tracking-[-0.03em] md:text-7xl" data-testid="world-heading">
              The tablet is the product.
              <span className="block text-[#C6FF3D]">This is what it plugs into.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-[#9AA8A0]" data-testid="world-intro">
              RydeCast is a moving ad network: brands pay to run ads on Slate 01 screens across Indian cities, and screen owners earn for verified playtime. The network only works because the hardware is real — that’s why Slate 01 comes first.
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <RydeCta href="/#order" testId="world-cta">Buy Now — ₹499</RydeCta>
              <GhostButton to="/" testId="world-back-link">Back to the tablet</GhostButton>
            </div>
          </RydeFade>
        </div>
      </section>

      <section className="border-t border-[#2A322E] bg-[#141816]/40 px-5 py-24 md:px-8" data-track-section="world-flow">
        <div className="mx-auto max-w-6xl">
          <RydeFade>
            <SectionTag>Money flow</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              Where the money comes from.
            </h2>
          </RydeFade>
          <RydeFade delay={0.08}>
            <div className="mt-10 flex flex-col items-stretch gap-3 md:flex-row md:items-center" data-testid="world-money-flow">
              {FLOW.map((step, i) => (
                <div key={step} className="flex flex-1 items-center gap-3">
                  <div className="flex-1 rounded-xl border border-[#2A322E] bg-[#0B0D0C] px-5 py-4 text-center font-display text-base font-bold" data-testid={`world-flow-step-${i}`}>
                    {step}
                  </div>
                  {i < FLOW.length - 1 && <span className="hidden font-display text-2xl font-black text-[#C6FF3D] md:block">→</span>}
                </div>
              ))}
            </div>
          </RydeFade>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {RULES.map(([Icon, title, text], i) => (
              <RydeFade key={title} delay={i * 0.08}>
                <BentoCard className="h-full" data-testid={`world-rule-${i}`}>
                  <Icon size={22} className="text-[#C6FF3D]" />
                  <p className="mt-4 font-display text-xl font-extrabold">{title}</p>
                  <p className="mt-2 text-sm leading-7 text-[#9AA8A0]">{text}</p>
                </BentoCard>
              </RydeFade>
            ))}
          </div>
          <RydeFade delay={0.15}>
            <p className="mt-8 max-w-3xl border-l-2 border-[#C6FF3D] pl-5 text-sm leading-7 text-[#9AA8A0]" data-testid="world-honesty">
              “Up to ₹15,000/month” describes a strong month with live campaigns in a busy city — never a salary. Some months will be lower. Anyone promising a fixed number is lying to you; we won’t.
            </p>
          </RydeFade>
        </div>
      </section>

      <section className="border-t border-[#2A322E] px-5 py-24 md:px-8" data-track-section="world-how">
        <div className="mx-auto max-w-6xl">
          <RydeFade>
            <SectionTag>How owners earn</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              Three steps. That’s it.
            </h2>
          </RydeFade>
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {STEPS.map(({ icon: Icon, title, text }, i) => (
              <RydeFade key={title} delay={i * 0.08}>
                <BentoCard className="h-full" data-testid={`world-step-${i}`}>
                  <p className="font-jbmono text-sm text-[#C6FF3D]">0{i + 1}</p>
                  <Icon size={26} className="mt-5 text-[#F4F7F5]" strokeWidth={1.6} />
                  <p className="mt-4 font-display text-2xl font-extrabold tracking-tight">{title}</p>
                  <p className="mt-3 text-sm leading-7 text-[#9AA8A0]">{text}</p>
                </BentoCard>
              </RydeFade>
            ))}
          </div>
          <RydeFade delay={0.12}>
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#9AA8A0]" data-testid="world-assurances">
              <span className="flex items-center gap-2"><BadgeCheck size={16} className="text-[#C6FF3D]" /> Verified playtime, not self-reported</span>
              <span className="flex items-center gap-2"><BadgeCheck size={16} className="text-[#C6FF3D]" /> Payouts on the 5th via UPI / bank</span>
              <span className="flex items-center gap-2"><BadgeCheck size={16} className="text-[#C6FF3D]" /> Owner app shows what played</span>
            </div>
          </RydeFade>
        </div>
      </section>

      <section className="border-t border-[#2A322E] px-5 py-24 md:px-8" data-track-section="world-final">
        <RydeFade>
          <div className="mx-auto max-w-6xl rounded-2xl border border-[#C6FF3D]/30 bg-[#141816] p-8 md:p-14" data-testid="world-final-card">
            <SectionTag>Join the network</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              It starts with the slate.
            </h2>
            <p className="mt-6 max-w-xl text-lg leading-8 text-[#9AA8A0]">
              Get Slate 01 with ₹499 today — delivered in 15–20 days, COD available. When campaigns go live in your city, your screen is ready.
            </p>
            <Link
              to="/#order"
              className="group mt-9 inline-flex items-center gap-2 rounded-xl bg-[#C6FF3D] px-7 py-4 font-display text-base font-extrabold text-[#0B0D0C] transition-colors duration-200 hover:bg-[#D4FF6B] active:scale-95"
              data-testid="world-final-cta"
            >
              Buy Now — ₹499
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </RydeFade>
      </section>

      <RydeFooter />
    </main>
  );
}
